import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import '../styles/components/FilterGroup.css';
import DateRangePicker from './common/DateRangePicker';
import TimeRangePicker from './common/TimeRangePicker';

interface GeneralFilterProps {
  key: string;
  value: string;
  placeholder: string;
}

interface MinMaxFilterProps {
  minKey: string;
  maxKey: string;
  minValue: string;
  maxValue: string;
  minLabel: string;
  maxLabel: string;
  type: 'text' | 'date' | 'time' | 'number';
}

interface FilterGroupProps {
  groupName: string;
  generalFilter: GeneralFilterProps;
  minMaxFilters: MinMaxFilterProps;
  onFilterChange: (key: string, value: any) => void;
  children?: React.ReactNode;
}

// Add ref interface for validation
export interface FilterGroupRef {
  validate: () => boolean;
}

const FilterGroup = forwardRef<FilterGroupRef, FilterGroupProps>(({
  groupName,
  generalFilter,
  minMaxFilters,
  onFilterChange,
  children
}, ref) => {
  const [activeMode, setActiveMode] = useState<'general' | 'minmax'>(
    generalFilter.value ? 'general' : 
    (minMaxFilters.minValue || minMaxFilters.maxValue) ? 'minmax' : 'general'
  );
  
  // Add validation state
  const [validationErrors, setValidationErrors] = useState<{
    min?: string;
    max?: string;
  }>({});
  
  // Clear validation errors when inputs change
  useEffect(() => {
    if (validationErrors.min || validationErrors.max) {
      setValidationErrors({});
    }
  }, [minMaxFilters.minValue, minMaxFilters.maxValue]);

  // Expose validate method to parent component
  useImperativeHandle(ref, () => ({
    validate: () => {
      // Only validate if in minmax mode
      if (activeMode === 'minmax') {
        return validateRange();
      }
      return true;
    }
  }));

  // Validation function
  const validateRange = () => {
    const errors: {min?: string; max?: string} = {};
    
    // Check if both values are provided
    if (!minMaxFilters.minValue && !minMaxFilters.maxValue) {
      // Both empty is OK (no filter)
      return true;
    } else if (!minMaxFilters.minValue) {
      errors.min = "Required";
      setValidationErrors(errors);
      return false;
    } else if (!minMaxFilters.maxValue) {
      errors.max = "Required";
      setValidationErrors(errors);
      return false;
    }
    
    // Check if To > From for different types
    if (minMaxFilters.type === 'date' || minMaxFilters.type === 'time') {
      const minDate = new Date(minMaxFilters.minValue);
      const maxDate = new Date(minMaxFilters.maxValue);
      
      if (maxDate < minDate) {
        errors.max = `Must be after ${minMaxFilters.minLabel}`;
      }
    } 
    else if (minMaxFilters.type === 'number') {
      const minNum = parseFloat(minMaxFilters.minValue);
      const maxNum = parseFloat(minMaxFilters.maxValue);
      
      if (maxNum < minNum) {
        errors.max = `Must be greater than ${minMaxFilters.minLabel}`;
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const switchToGeneral = () => {
    if (activeMode !== 'general') {
      setValidationErrors({});
      onFilterChange(minMaxFilters.minKey, '');
      onFilterChange(minMaxFilters.maxKey, '');
      setActiveMode('general');
    }
  };

  const switchToMinMax = () => {
    if (activeMode !== 'minmax') {
      setValidationErrors({});
      onFilterChange(generalFilter.key, '');
      setActiveMode('minmax');
    }
  };

  // Determine error class for inputs
  const getErrorClass = (field: 'min' | 'max') => {
    return validationErrors[field] ? 'filter-input-error' : '';
  };

  return (

    <div className="filter-group">
      <div className="filter-group-header">
        <h4>{groupName}</h4>
        <div className="filter-toggle">
          <button 
            className={`toggle-button ${activeMode === 'general' ? 'active' : ''}`}
            onClick={switchToGeneral}
          >
            Simple
          </button>
          <button 
            className={`toggle-button ${activeMode === 'minmax' ? 'active' : ''}`}
            onClick={switchToMinMax}
          >
            Range
          </button>
        </div>
      </div>

      <div className="filter-group-content">
        {activeMode === 'general' ? (
          <div className="general-filter">
            {generalFilter.key.toLowerCase().includes('date') ? (
              <DateRangePicker
                startDate={generalFilter.value}
                endDate=""
                onStartDateChange={(date) => onFilterChange(generalFilter.key, date)}
                onEndDateChange={() => {}}
                startLabel="Enter Date"
                endLabel=""
                isSingleDate={true}
              />
            ) : generalFilter.key.toLowerCase().includes('time') ? (
              <TimeRangePicker
                startTime={generalFilter.value}
                endTime=""
                onStartTimeChange={(time) => onFilterChange(generalFilter.key, time)}
                onEndTimeChange={() => {}}
                startLabel="Enter Time"
                endLabel=""
                isSingleTime={true}
              />
            ) : (
              <input
                type="text"
                value={generalFilter.value}
                onChange={(e) => onFilterChange(generalFilter.key, e.target.value)}
                placeholder={generalFilter.placeholder}
                className="filter-input"
              />
            )}
          </div>
        ) : (
            <div className="filter-row-inline">

            {minMaxFilters.type === 'date' ? (
              <DateRangePicker
                startDate={minMaxFilters.minValue}
                endDate={minMaxFilters.maxValue}
                onStartDateChange={(date) => onFilterChange(minMaxFilters.minKey, date)}
                onEndDateChange={(date) => onFilterChange(minMaxFilters.maxKey, date)}
                startLabel={minMaxFilters.minLabel}
                endLabel={minMaxFilters.maxLabel}
                startInputClassName={getErrorClass('min')}
                endInputClassName={getErrorClass('max')}
                startError={validationErrors.min}
                endError={validationErrors.max}
                isSingleDate={false}
              />
            ) : minMaxFilters.type === 'time' ? (
              <TimeRangePicker
                startTime={minMaxFilters.minValue}
                endTime={minMaxFilters.maxValue}
                onStartTimeChange={(time) => onFilterChange(minMaxFilters.minKey, time)}
                onEndTimeChange={(time) => onFilterChange(minMaxFilters.maxKey, time)}
                startLabel={minMaxFilters.minLabel}
                endLabel={minMaxFilters.maxLabel}
                startInputClassName={getErrorClass('min')}
                endInputClassName={getErrorClass('max')}
                startError={validationErrors.min}
                endError={validationErrors.max}
                isSingleTime={false}
              />
            ) : (
              <div className="min-max-container">
                <div className="min-filter">
                  <div className="filter-label">
                    <span className="label-with-error">
                      {minMaxFilters.minLabel}
                      {validationErrors.min && <span className="inline-error">{validationErrors.min}</span>}
                    </span>
                  </div>
                  <input
                    type={minMaxFilters.type}
                    value={minMaxFilters.minValue}
                    onChange={(e) => onFilterChange(minMaxFilters.minKey, e.target.value)}
                    className={`filter-input ${getErrorClass('min')}`}
                  />
                </div>
                <div className="max-filter">
                  <div className="filter-label">
                    <span className="label-with-error">
                      {minMaxFilters.maxLabel}
                      {validationErrors.max && <span className="inline-error">{validationErrors.max}</span>}
                    </span>
                  </div>
                  <input
                    type={minMaxFilters.type}
                    value={minMaxFilters.maxValue}
                    onChange={(e) => onFilterChange(minMaxFilters.maxKey, e.target.value)}
                    className={`filter-input ${getErrorClass('max')}`}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {children}
      </div>
    </div>
  );
});

export default FilterGroup; 